import { useUserStore } from '@/stores/modules/user'
import type { EquipAffixType } from '@/types/equip-affix'
import { runeSpecialFactor } from '../rune'
import { formatNumber } from '@/utils'
import type { EquipSpecialAttrType } from '@/types/equip'

export const equipAffixCollection: EquipAffixType = {
  thump: {
    name: '重击',
    desc: ({ rate, value, multiplier, extraRate = 0, extraDamage = 0, soulType }) => {
      const mag = soulType === 1 ? 1.2 : 1
      const realExtraRate = Number((extraRate * runeSpecialFactor.thump.extraRate * mag).toFixed(2))
      const realExtraDamage = Number((extraDamage * runeSpecialFactor.thump.extraDamage * mag).toFixed(2))
      if (multiplier) {
        return `<span class="info">${formatNumber(rate * (1 + realExtraRate), 2)}%</span>${
          realExtraRate > 0 ? `<span class="awaken">(+ ${formatNumber(rate * realExtraRate, 2)}%)</span>` : ''
        } 概率额外造成当前攻击力 <span class="grow">${formatNumber(multiplier * (1 + realExtraDamage), 2)}%</span>${
          realExtraDamage > 0 ? `<span class="awaken">(+ ${formatNumber(multiplier * realExtraDamage, 2)}%)</span>` : ''
        } 的伤害`
      } else {
        return `<span class="info">${formatNumber(rate * (1 + realExtraRate), 2)}%</span>${
          realExtraRate > 0 ? `<span class="awaken">(+ ${formatNumber(rate * realExtraRate, 2)}%)</span>` : ''
        } 概率额外造成 <span class="grow">${Math.round(value * (1 + realExtraDamage))}</span>${
          realExtraDamage > 0 ? `<span class="awaken">(+ ${Math.round(value * realExtraDamage)})</span>` : ''
        } 伤害`
      }
    },
  },
  harvest: {
    name: '收割',
    desc: ({ hpPercent, value, multiplier, extraHpPercent = 0, extraMultiplier = 0, soulType }) => {
      const mag = soulType === 3 ? 1.2 : 1
      const realExtraHpPercent = Number((extraHpPercent * runeSpecialFactor.harvest.extraHpPercent * mag).toFixed(2))
      const realExtraMultiplier = Number((extraMultiplier * runeSpecialFactor.harvest.extraMultiplier * mag).toFixed(2))
      if (multiplier) {
        return `敌方血量低于 <span class="info">${formatNumber(hpPercent * (1 + realExtraHpPercent), 2)}%</span>${
          realExtraHpPercent > 0 ? `<span class="awaken">(+ ${formatNumber(hpPercent * realExtraHpPercent, 2)}%)</span>` : ''
        } 时，额外造成 <span class="grow">${formatNumber(multiplier * (1 + realExtraMultiplier), 2)}%</span>${
          realExtraMultiplier > 0 ? `<span class="awaken">(+ ${formatNumber(multiplier * realExtraMultiplier, 2)}%)</span>` : ''
        } 的伤害`
      } else {
        return `敌方血量低于 <span class="info">${formatNumber(hpPercent * (1 + realExtraHpPercent), 2)}%</span>${
          realExtraHpPercent > 0 ? `<span class="awaken">(+ ${formatNumber(hpPercent * realExtraHpPercent, 2)}%)</span>` : ''
        } 时，额外造成 <span class="grow">${Math.round(value * (1 + realExtraMultiplier))}</span>${
          realExtraMultiplier > 0 ? `<span class="awaken">(+ ${Math.round(value * realExtraMultiplier)})</span>` : ''
        } 伤害`
      }
    },
  },
  assault: {
    name: '冲锋',
    desc: ({ hpPercent, value, multiplier, extraHpPercent = 0, extraMultiplier = 0, soulType }) => {
      const mag = soulType === 1 ? 1.2 : 1
      const realExtraHpPercent = Number((extraHpPercent * runeSpecialFactor.assault.extraHpPercent * mag).toFixed(2))
      const realExtraMultiplier = Number((extraMultiplier * runeSpecialFactor.assault.extraMultiplier * mag).toFixed(2))
      if (multiplier) {
        return `敌方血量高于 <span class="info">${formatNumber(hpPercent - (100 - hpPercent) * realExtraHpPercent, 2)}%</span>${
          realExtraHpPercent > 0 ? `<span class="awaken">(- ${formatNumber((100 - hpPercent) * realExtraHpPercent, 2)}%)</span>` : ''
        } 时，额外造成 <span class="grow">${formatNumber(multiplier * (1 + realExtraMultiplier), 2)}%</span>${
          realExtraMultiplier > 0 ? `<span class="awaken">(+ ${formatNumber(multiplier * realExtraMultiplier, 2)}%)</span>` : ''
        } 的伤害`
      } else {
        return `敌方血量高于 <span class="info">${formatNumber(hpPercent - (100 - hpPercent) * realExtraHpPercent, 2)}%</span>${
          realExtraHpPercent > 0 ? `<span class="awaken">(- ${formatNumber((100 - hpPercent) * realExtraHpPercent, 2)}%)</span>` : ''
        } 时，额外造成 <span class="grow">${Math.round(value * (1 + realExtraMultiplier))}</span>${
          realExtraMultiplier > 0 ? `<span class="awaken">(+ ${Math.round(value * (1 + realExtraMultiplier))})</span>` : ''
        } 伤害`
      }
    },
  },
  chasing: {
    name: '追击',
    desc: ({ value, extraValue = 0, soulType }) => {
      const mag = soulType === 3 ? 1.2 : 1
      const realExtraValue = Math.round(extraValue * runeSpecialFactor.chasing.extraValue * mag)
      return `每次攻击时，额外造成 <span class="grow">${Math.round(value + realExtraValue)}</span>${
        realExtraValue > 0 ? `<span class="awaken">(+ ${realExtraValue})</span>` : ''
      } 伤害`
    },
  },
  heavyInjury: {
    name: '重创',
    desc: ({ value, extraValue = 0, soulType }) => {
      const mag = soulType === 1 ? 1.2 : 1
      const realExtraValue = Math.round(extraValue * runeSpecialFactor.heavyInjury.extraValue * mag)
      return `暴击时，额外造成 <span class="grow">${Math.round(value + realExtraValue)}</span>${
        realExtraValue > 0 ? `<span class="awaken">(+ ${realExtraValue})</span>` : ''
      } 伤害`
    },
  },
  break: {
    name: '破阵',
    desc: ({ multiplier, extraMultiplier = 0, soulType }) => {
      const mag = soulType === 1 ? 1.2 : 1
      const realExtraMultiplier = Number((extraMultiplier * runeSpecialFactor.break.extraMultiplier * mag).toFixed(2))
      return `攻击力提高 <span class="grow">${formatNumber(multiplier + realExtraMultiplier, 2)}%</span>${
        realExtraMultiplier ? `<span class="awaken">(+ ${realExtraMultiplier}%)</span>` : ''
      }`
    },
  },
  crushBone: {
    name: '碎骨',
    desc: ({ rate, value, multiplier, extraRate = 0, extraMag = 0, soulType }) => {
      const mag = soulType === 3 ? 1.2 : 1
      const realExtraRate = Number((extraRate * runeSpecialFactor.crushBone.extraRate * mag).toFixed(2))
      const realExtraMag = Number((extraMag * runeSpecialFactor.crushBone.extraMag * mag).toFixed(2))
      if (value) {
        return `<span class="info">${formatNumber(rate * (1 + realExtraRate), 2)}%</span>${
          realExtraRate > 0 ? `<span class="awaken">(+ ${formatNumber(rate * realExtraRate, 2)}%)</span>` : ''
        } 概率忽略敌方 <span class="grow">${Math.round(value * (1 + realExtraMag))}</span>${
          realExtraMag > 0 ? `<span class="awaken">(+ ${Math.round(value * realExtraMag)})</span>` : ''
        } 防御`
      } else {
        return `<span class="info">${formatNumber(rate * (1 + realExtraRate), 2)}%</span>${
          realExtraRate > 0 ? `<span class="awaken">(+ ${formatNumber(rate * realExtraRate, 2)}%)</span>` : ''
        } 概率忽略敌方 <span class="grow">${formatNumber(multiplier * (1 + realExtraMag), 2)}%</span>${
          realExtraMag > 0 ? `<span class="awaken">(+ ${formatNumber(multiplier * realExtraMag, 2)}%)</span>` : ''
        } 的防御`
      }
    },
  },
  burst: {
    name: '爆发',
    desc: ({ rate, extraRate = 0, soulType }) => {
      const mag = soulType === 2 ? 1.2 : 1
      const realExtraRate = Number((extraRate * runeSpecialFactor.burst.extraRate * mag).toFixed(2))
      return `攻击未暴击时，额外进行一次暴击判定，概率为 <span class="info">${formatNumber(rate + realExtraRate, 2)}%${
        realExtraRate > 0 ? `<span class="awaken">(+ ${realExtraRate}%)</span>` : ''
      }</span>`
    },
  },
  nothingness: {
    name: '虚无',
    desc: ({ multiplier, extraMultiplier = 0, soulType }) => {
      const mag = soulType === 2 ? 1.2 : 1
      const realExtraMultiplier = Number((runeSpecialFactor.nothingness.extraMultiplier * mag * extraMultiplier).toFixed(2))
      return `<span class="info">${formatNumber(multiplier + realExtraMultiplier, 2)}%</span>${
        realExtraMultiplier ? `<span class="awaken">(+ ${realExtraMultiplier}%)</span>` : ''
      } 的伤害转换为<span class="real-damage">真实伤害</span>`
    },
  },
  split: {
    name: '分裂',
    desc: ({ rate, value, extraRate = 0, soulType }) => {
      const mag = soulType === 2 ? 1.2 : 1
      const realExtraRate = Number((runeSpecialFactor.split.extraRate * mag * extraRate).toFixed(2))
      if (rate === 100) {
        return `攻击分裂为 <span class="info">${value + 1}</span> 段伤害${
          realExtraRate > 0
            ? `，<span class="awaken">${formatNumber(realExtraRate * 100, 2)}%</span>概率额外分裂 <span class="info">${value}</span>段`
            : ''
        }`
      } else {
        const isOver = rate * (1 + realExtraRate) > 100
        if (isOver) {
          const overRate = rate * (1 + realExtraRate) - 100
          return `攻击分裂为 <span class="info">${value + 1}</span> 段伤害${
            realExtraRate > 0 ? `，<span class="awaken">${formatNumber(overRate, 2)}%</span>概率额外分裂 <span class="info">${value}</span> 段` : ''
          }`
        } else {
          return `<span class="info">${formatNumber(rate * (1 + realExtraRate), 2)}%</span>${
            realExtraRate > 0 ? `<span class="awaken">(+ ${formatNumber(rate * realExtraRate, 2)}%)</span>` : ''
          } 概率，攻击分裂为 <span class="info">${value + 1}</span> 段伤害`
        }
      }
    },
  },
  cruel: {
    name: '残忍',
    desc: ({ value, multiplier, extraValue = 0, extraMultiplier = 0, soulType }) => {
      const mag = soulType === 1 ? 1.2 : 1
      const realExtraValue = Math.round(runeSpecialFactor.cruel.extraValue * mag * extraValue)
      const realExtraMultiplier = Number((runeSpecialFactor.cruel.extraMultiplier * mag * extraMultiplier).toFixed(2))
      if (value) {
        return `暴击时，忽略敌方 <span class="grow">${Math.round(value + realExtraValue)}</span>${
          realExtraValue > 0 ? `<span class="awaken">(+ ${realExtraValue})</span>` : ''
        }${
          realExtraMultiplier > 0 ? ` + <span class="grow">${realExtraMultiplier}%</span><span class="awaken">(+ ${realExtraMultiplier}%)</span>` : ''
        } 防御`
      } else {
        return `暴击时，忽略敌方 <span class="grow">${formatNumber(multiplier + realExtraMultiplier, 2)}%${
          realExtraMultiplier > 0 ? `<span class="awaken">(+ ${realExtraMultiplier}%)</span>` : ''
        }${realExtraValue > 0 ? ` + <span class="grow">${realExtraValue}</span><span class="awaken">(+ ${realExtraValue})</span>` : ''}</span> 的防御`
      }
    },
  },
  shadowBlade: {
    name: '影刃',
    desc: ({ value, multiplier, extraValue = 0, extraMultiplier = 0, soulType }) => {
      const mag = soulType === 2 ? 1.2 : 1
      const realExtraValue = Math.round(runeSpecialFactor.shadowBlade.extraValue * mag * extraValue)
      const realExtraMultiplier = Number((runeSpecialFactor.shadowBlade.extraMultiplier * mag * extraMultiplier).toFixed(2))
      if (value) {
        return `每次攻击时，额外造成 <span class="grow">${formatNumber(value + realExtraValue)}</span>${
          realExtraValue > 0 ? `<span class="awaken">(+${realExtraValue})</span>` : ''
        }${
          realExtraMultiplier > 0 ? ` + <span class="grow">${realExtraMultiplier}%</span><span class="awaken">(+ ${realExtraMultiplier}%)</span>` : ''
        } <span class="real-damage">真实伤害</span>`
      } else {
        return `每次攻击时，额外造成 <span class="grow">${formatNumber(multiplier + realExtraMultiplier, 2)}%</span>${
          realExtraMultiplier > 0 ? `<span class="awaken">(+ ${realExtraMultiplier}%)</span>` : ''
        }${
          realExtraValue > 0 ? ` + <span class="grow">${realExtraValue}</span><span class="awaken">(+ ${realExtraValue})</span>` : ''
        } 的<span class="real-damage">真实伤害</span>`
      }
    },
  },
  swiftness: {
    name: '轻灵',
    desc: ({ value, extraValue = 0, soulType }) => {
      const mag = soulType === 3 ? 1.2 : 1
      const realExtraValue = Number((runeSpecialFactor.swiftness.extraValue * mag * extraValue).toFixed(2))
      return `额外攻击速度增加 <span class="grow">${formatNumber(value - 1 + realExtraValue, 2)}${
        realExtraValue > 0 ? `<span class="awaken">(+${realExtraValue})</span>` : ''
      }</span> 倍`
    },
  },
  sharp: {
    name: '锋利',
    desc: ({ multiplier }) => {
      return `攻击时，附加 <span class="grow">${multiplier}%</span> 的伤害`
    },
  },
  precise: {
    name: '精准',
    desc: ({ multiplier, extraMultiplier = 0, soulType }) => {
      const mag = soulType === 3 ? 1.2 : 1
      const realExtraMultiplier = Number((runeSpecialFactor.precise.extraMultiplier * mag * extraMultiplier).toFixed(2))
      return `降低敌方闪避率的 <span class="grow">${formatNumber(multiplier + realExtraMultiplier, 2)}%${
        realExtraMultiplier > 0 ? `<span class="awaken">(+ ${realExtraMultiplier}%)</span>` : ''
      }</span>`
    },
  },
  rout: {
    name: '击溃',
    desc: ({ value, multiplier, extraValue = 0, extraMultiplier = 0, soulType }) => {
      const mag = soulType === 2 ? 1.2 : 1
      const realExtraValue = Number((runeSpecialFactor.rout.extraValue * mag * extraValue).toFixed(2))
      const realExtraMultiplier = Number((runeSpecialFactor.rout.extraMultiplier * mag * extraMultiplier).toFixed(2))
      if (value) {
        return `减少敌方 <span class="grow">${formatNumber(value + realExtraValue, 2)}%</span>${
          realExtraValue > 0 ? `<span class="awaken">(+${realExtraValue})</span>` : ''
        } 抗暴率`
      } else {
        return `降低敌方抗暴率的 <span class="grow">${formatNumber(multiplier + realExtraMultiplier, 2)}%</span>${
          realExtraMultiplier > 0 ? `<span class="awaken">(+ ${realExtraMultiplier}%)</span>` : ''
        }`
      }
    },
  },
  tearInjury: {
    name: '裂创',
    desc: ({ value, extraValue = 0, soulType }) => {
      const mag = soulType === 2 ? 1.2 : 1
      const realExtraValue = Math.round(runeSpecialFactor.tearInjury.extraValue * mag * extraValue)
      return `暴击时，额外造成 <span class="grow">${Math.round(value + realExtraValue)}</span>${
        realExtraValue > 0 ? `<span class="awaken">(+${realExtraValue})</span>` : ''
      } <span class="real-damage">真实伤害</span>`
    },
  },
  impact: {
    name: '冲击',
    desc: ({ hpPercent, value, multiplier, extraHpPercent = 0, extraMag = 0, soulType }) => {
      const mag = soulType === 1 ? 1.2 : 1
      const realExtraHpPercent = Number((extraHpPercent * runeSpecialFactor.assault.extraHpPercent * mag).toFixed(2))
      const realExtraMag = Number((extraMag * runeSpecialFactor.assault.extraMultiplier * mag).toFixed(2))
      if (multiplier) {
        return `敌方血量高于 <span class="info">${formatNumber(hpPercent - (100 - hpPercent) * realExtraHpPercent, 2)}%</span>${
          realExtraHpPercent > 0 ? `<span class="awaken">(- ${formatNumber((100 - hpPercent) * realExtraHpPercent, 2)}%)</span>` : ''
        } 时，忽略敌方 <span class="grow">${formatNumber(multiplier * (1 + realExtraMag), 2)}%</span>${
          realExtraMag > 0 ? `<span class="awaken">(+ ${formatNumber(multiplier * realExtraMag, 2)}%)</span>` : ''
        } 的防御`
      } else {
        return `敌方血量高于 <span class="info">${formatNumber(hpPercent - (100 - hpPercent) * realExtraHpPercent, 2)}%</span>${
          realExtraHpPercent > 0 ? `<span class="awaken">(- ${formatNumber((100 - hpPercent) * realExtraHpPercent, 2)}%)</span>` : ''
        } 时，忽略敌方 <span class="grow">${Math.round(value * (1 + realExtraMag))}</span>${
          realExtraMag > 0 ? `<span class="awaken">(+ ${Math.round(value * realExtraMag)})</span>` : ''
        } 防御`
      }
    },
  },
  windUp: {
    name: '收尾',
    desc: ({ hpPercent, value, multiplier, extraHpPercent = 0, extraMag = 0, soulType }) => {
      const mag = soulType === 3 ? 1.2 : 1
      const realExtraHpPercent = Number((runeSpecialFactor.impact.extraHpPercent * mag * extraHpPercent).toFixed(2))
      const realExtraMag = Number((runeSpecialFactor.impact.extraMag * mag * extraMag).toFixed(2))
      if (multiplier) {
        return `敌方血量低于 <span class="info">${formatNumber(hpPercent * (1 + realExtraHpPercent), 2)}%</span>${
          realExtraHpPercent > 0 ? `<span class="awaken">(+ ${formatNumber(hpPercent * realExtraHpPercent, 2)}%)</span>` : ''
        } 时，忽略敌方 <span class="grow">${formatNumber(multiplier * (1 + realExtraMag), 2)}%</span>${
          realExtraMag > 0 ? `<span class="awaken">(+ ${formatNumber(multiplier * realExtraMag, 2)}%)</span>` : ''
        } 的防御`
      } else {
        return `敌方血量低于 <span class="info">${formatNumber(hpPercent * (1 + realExtraHpPercent), 2)}%</span>${
          realExtraHpPercent > 0 ? `<span class="awaken">(+ ${formatNumber(hpPercent * realExtraHpPercent, 2)}%)</span>` : ''
        } 时，忽略敌方 <span class="grow">${formatNumber(value * (1 + realExtraMag), 2)}</span>${
          realExtraMag > 0 ? `<span class="awaken">(+ ${formatNumber(value * realExtraMag, 2)})</span>` : ''
        } 防御`
      }
    },
  },
  swanElegy: {
    name: '噩兆',
    desc: ({ hpPercent }) => `敌方血量低于 <span class="info">${hpPercent}%</span> 时，必定暴击`,
  },
  lucky7: {
    name: '幸运7',
    desc: ({ rate }) => `攻击时暴击率固定为 <span class="info">${rate}%</span>`,
  },
  drawChop: {
    name: '拔刀斩',
    desc: ({ atkPercent }) =>
      `攻击速度 <span class="shrink">减半</span>，攻击 + <span class="grow">${formatNumber(
        (atkPercent - 1) * 100,
      )}%</span>，命中 + <span class="grow">20%</span>，暴击 + <span class="grow">20%</span>`,
  },
  onikiri: {
    name: '鬼斩',
    desc: ({ rate }) => `<span class="info">${rate}%</span> 概率，将所有伤害转为<span class="real-damage">真实伤害</span>`,
  },
  longYa: {
    name: '暴虐',
    desc: ({ percent }) => `<span class="info">${percent * 100}%</span> 的破防转换为攻击`,
  },
  desolator: {
    name: '黯灭',
    desc: ({ crtd }) => `暴击伤害固定为 <span class="info">${crtd}%</span>`,
  },
  gentle: {
    name: '温柔一刀',
    desc: () => '暴击时，<span class="shrink">无法</span>造成伤害',
  },
  nightingale: {
    name: '夜莺',
    desc: () => '获得等同于 <span class="info">暴击率</span> 的 <span class="info">攻击</span>',
  },
  balance: {
    name: '神圣平衡',
    desc: () => '将 <span class="info">额外攻击力</span> 与 <span class="info">破防</span> 互换',
  },
  pity: {
    name: '怜悯',
    desc: ({ value }) => `命中率固定为 <span class="info">${value}%</span>`,
  },
  parocheth: {
    name: '帷幕',
    desc: ({ hpPercent }) => `敌方血量低于 <span class="info">${hpPercent}%</span> 时，必定命中`,
  },
  shock: {
    name: '震慑',
    desc: ({ multiplier, mag }) =>
      `若<span class="info">攻击大于敌方 ${mag}倍防御</span>时，忽略敌方 <span class="grow">${multiplier}%</span> 的防御`,
  },
  sigh: {
    name: '扼腕',
    desc: ({ hpPercent, multiplier }) =>
      `敌方血量低于 <span class="info">${hpPercent}%</span> 时，额外造成 <span class="grow">${multiplier}%</span> 的<span class="real-damage">真实伤害</span>`,
  },
  cease: {
    name: '止息',
    desc: ({ multiplier }) => `若<span class="info">结算命中率大于 100%</span>时，额外造成 <span class="grow">${multiplier}%</span> 的伤害`,
  },
  shudder: {
    name: '震撼',
    desc: ({ multiplier }) => `若<span class="info">结算暴击率大于 100%</span>时，额外造成 <span class="grow">${multiplier}%</span> 的伤害`,
  },
  secretLucky7: {
    name: '秘 · 幸运7',
    desc: () =>
      '暴击率固定为 <span class="info">7.77%</span>，与<span class="info">原暴击率</span>相比，每降低 <span class="shrink">1%</span>暴击率，则获得 <span class="grow">2%</span>暴击伤害',
  },
  secretPity: {
    name: '秘 · 怜悯',
    desc: ({}) =>
      '命中率固定为 <span class="info">80%</span>，与<span class="info">原命中率</span>相比，每降低 <span class="shrink">1%</span>命中率，则获得 <span class="grow">1</span>破防',
  },
  mockMoon: {
    name: '幻月',
    desc: ({ mag }) => `获得 <span class="grow">${mag}</span> 倍<span class="info">额外攻击速度</span> 的<span class="info">命中率</span>`,
  },
  slaughter: {
    name: '屠戮',
    desc: ({ hpPercent }) => `敌方血量低于 <span class="info">${hpPercent}%</span> 时，无视防御`,
  },
  fate: {
    name: '命运',
    desc: ({ min, max }) => `攻击命中时，将随机造成其伤害的 <span class="info">${min}% ~ ${max}%</span>`,
  },
  focus: {
    name: '青羽',
    desc: ({ mag }) => `<span class="info">暴击率</span>及<span class="info">额外暴击伤害</span>增加 <span class="grow">${mag}</span>倍`,
  },
  fallDream: {
    name: '入梦',
    desc: ({ mag }) =>
      `攻击<span class="info">未命中</span>时，造成当前攻击力 <span class="grow">${mag * 100}%</span> 的<span class="legend">直接伤害</span>`,
  },
  tranquil: {
    name: '安谧',
    desc: () => '将 <span class="info">一半</span>的<span class="info">暴击率</span>转换为<span class="info">命中率</span>',
  },
  shadowHit: {
    name: '影袭',
    desc: ({ multiplier }) => `<span class="real-damage">真实伤害</span>增加 <span class="grow">${multiplier}%</span>`,
  },
  honor: {
    name: '荣耀',
    desc: ({ multiplier }) =>
      `当造成特效仅包含 <span class="info">荣耀</span>时，增加 <span class="grow">${multiplier}%</span>伤害，反之降低 <span class="shrink">${multiplier}%</span>`,
  },
  bloodKiss: {
    name: '血吻',
    desc: ({ mag }) => `攻击时，获得敌方 <span class="grow">${mag}</span>倍<span class="info">闪避值</span>的破防`,
  },
  fireHeart: {
    name: '心火',
    desc: ({ mag }) => `每次攻击时，额外造成 <span class="grow">${mag}</span>倍敌方<span class="info">抗暴值</span>的伤害`,
  },
}

export function explainEquipAffix(affixList: EquipSpecialAttrType[], showExtra: boolean = true, showPrefix: boolean = true) {
  const userStore = useUserStore()
  const extraAttrs = showExtra ? userStore.getExtraAttrs : {}
  return affixList.map((item) => {
    const equipAffix = equipAffixCollection[item.key]
    const name = equipAffix.name
    const desc = equipAffix.desc({ ...item.data, ...extraAttrs[item.key], soulType: userStore.soulType })
    if (!showPrefix) return desc
    return `<span class="special">${name}</span>：${desc}`
  })
}

export function getAllEquipAffix() {
  return Object.entries(equipAffixCollection).map(([key, item]) => {
    return {
      key,
      name: item.name,
    }
  })
}
